import { Injectable } from "@angular/core";

import {
  GEOFENCING_AREA_TOO_LARGE_MESSAGE,
  GEOFENCING_REQUIRED_DETAIL,
  GEOFENCING_REQUIRED_MESSAGE,
  geofencingAreaTooLargeDetail,
  IGeofencingArea,
  IGeofencingSearchValidation,
  MAX_GEOFENCING_AREA_SQUARE_METERS,
  SearchFilters,
} from "../../../model";
import { GeoService } from "../../../../viewer-components/services/internal-services/geo.service";

/** Geofencing geometry as configured by the user, before it becomes a search filter. */
export interface IGeofencingGeometryInput {
  /** WKT geometry: a polygon area, or the center point of a point + radius selection */
  wkt?: string | null;
  /** Radius in meters when the geometry is a point + radius selection */
  radiusMeters?: number | null;
}

/**
 * Validates the geofencing precondition of the embeddings search: a geofencing area is
 * mandatory, and it must stay under {@link MAX_GEOFENCING_AREA_SQUARE_METERS} — the API
 * cannot process larger areas.
 *
 * All members are static so components, dialogs and services can share the exact same rules.
 */
@Injectable({
  providedIn: "root",
})
export class GeofencingValidationService {
  /**
   * Formats an area for display, switching to square meters for very small areas so they
   * don't collapse to "0 km²".
   * @param squareMeters Area in square meters
   * @returns Human-readable area with unit, e.g. "1,250 km²"
   */
  static formatArea(squareMeters: number): string {
    if (!Number.isFinite(squareMeters) || squareMeters <= 0) {
      return "0 km²";
    }

    const squareKilometers = squareMeters / 1_000_000;
    if (squareKilometers < 0.01) {
      return `${Math.round(squareMeters).toLocaleString("en-US")} m²`;
    }

    const decimals = squareKilometers < 1 ? 3 : squareKilometers < 100 ? 2 : 0;
    return `${squareKilometers.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })} km²`;
  }

  /**
   * Describes an area measurement, including whether it is over the API limit.
   * @param squareMeters Area in square meters
   * @returns Area description, or null when there is no measurable area
   */
  static describeArea(squareMeters: number): IGeofencingArea | null {
    if (!Number.isFinite(squareMeters) || squareMeters <= 0) {
      return null;
    }

    return {
      squareMeters,
      squareKilometers: squareMeters / 1_000_000,
      exceedsLimit: squareMeters > MAX_GEOFENCING_AREA_SQUARE_METERS,
      formatted: GeofencingValidationService.formatArea(squareMeters),
    };
  }

  /**
   * Measures a geofencing geometry. A radius takes precedence over the WKT itself, since a
   * point + radius selection stores only the center point as WKT.
   * @param geometry Geofencing geometry to measure
   * @returns Area description, or null when the geometry has no measurable area
   */
  static measureArea(geometry: IGeofencingGeometryInput | null | undefined): IGeofencingArea | null {
    if (!geometry?.wkt) {
      return null;
    }

    const radiusArea = geometry.radiusMeters ? GeoService.circleArea(geometry.radiusMeters) : 0;
    const geometryArea = GeoService.wktArea(geometry.wkt);

    return GeofencingValidationService.describeArea(Math.max(radiusArea, geometryArea));
  }

  /**
   * Validates a geofencing geometry against the search preconditions.
   * @param geometry Geofencing geometry to validate, or null/undefined when none is configured
   * @returns Validation outcome with a user-facing reason when invalid
   */
  static validateGeometry(geometry: IGeofencingGeometryInput | null | undefined): IGeofencingSearchValidation {
    if (!geometry?.wkt) {
      return { valid: false, reason: GEOFENCING_REQUIRED_DETAIL, area: null };
    }

    const area = GeofencingValidationService.measureArea(geometry);
    if (area?.exceedsLimit) {
      return { valid: false, reason: geofencingAreaTooLargeDetail(area.formatted), area };
    }

    return { valid: true, reason: null, area };
  }

  /**
   * Validates the geofencing currently applied to the search filters — the geometry that
   * would actually be sent to the API.
   * @param filters Current search filters
   * @returns Validation outcome with a user-facing reason when invalid
   */
  static validateFilters(filters: SearchFilters | null | undefined): IGeofencingSearchValidation {
    return GeofencingValidationService.validateGeometry({
      wkt: filters?.wkt,
      radiusMeters: filters?.["buffer-meter"],
    });
  }

  /**
   * Short label for a disabled search control, suitable for a tooltip or inline hint.
   * @param validation Validation outcome to summarize
   * @returns Short label, or null when the validation is valid
   */
  static shortReason(validation: IGeofencingSearchValidation): string | null {
    if (validation.valid) {
      return null;
    }
    return validation.area?.exceedsLimit ? GEOFENCING_AREA_TOO_LARGE_MESSAGE : GEOFENCING_REQUIRED_MESSAGE;
  }
}
